/**
 * One-shot spoken-rundown player for the Quality Co-Pilot stream.
 *
 * Kept in a sibling `.ts` (not the page `.tsx`) so the playback rule is pure and
 * unit-testable, and so the page module exports only its component (the
 * react-refresh ESLint rule).
 *
 * The rule (the user's ask): the spoken rundown must start playing the MOMENT its
 * `voice_reply` frame streams in — so it speaks while the agent is still writing
 * the report — NOT on the end-of-turn `done` event. It must play at most ONCE per
 * turn (a second `voice_reply`, or any later frame, must not restart/overlap it),
 * must respect the auto-play toggle, and must not play after an error (and should
 * stop any in-flight playback when an error arrives).
 */
import type { ConsoleEvent } from '../services/agentConsole';
import { isVoiceReplyTool, spokenSummaryFromToolUse } from '../services/qualityCopilot';

export interface RundownPlayerOptions {
  /** Read the live auto-play toggle (a ref getter, so it is always current). */
  autoplay: () => boolean;
  /** Start playback of the spoken rundown (stop any prior audio + speak + mark). */
  onPlay: (spoken: string) => void;
  /** Stop playback (on an error frame). */
  onStop: () => void;
}

/**
 * Build a per-turn event handler. Feed it every streamed {@link ConsoleEvent};
 * it fires `onPlay` once at the first non-empty `voice_reply` summary (when
 * auto-play is on and no error has occurred), and `onStop` on an error frame.
 */
export function makeRundownPlayer(
  opts: RundownPlayerOptions,
): (evt: ConsoleEvent) => void {
  let played = false;
  let errored = false;
  return (evt: ConsoleEvent) => {
    if (evt.type === 'tool_use' && isVoiceReplyTool(evt.name)) {
      const spoken = spokenSummaryFromToolUse(evt);
      if (spoken && opts.autoplay() && !played && !errored) {
        played = true;
        opts.onPlay(spoken);
      }
    } else if (evt.type === 'error') {
      errored = true;
      opts.onStop();
    }
  };
}
